import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMeetingDashboardData } from "@/hooks/useMeetingDashboardData";
import { useScriptDashboardOverview } from "@/hooks/useTenantDashboardService";
import { useDashboardTheme, getBadgeTheme } from "./themeHelper";
import { isMeetingRecorded } from "@/utils/meetingFilter";


interface AllActivityPageProps {
  onBack: () => void;
  userData: any;
}

function getInitials(name: string = "") {
  return name.trim().split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = ["#6C63FF", "#FF6584", "#43C6AC", "#F7971E", "#56CCF2", "#BB6BD9", "#F2994A", "#27AE60"];
function avatarColor(name: string = "") {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function Avatar({ name, size = 34 }: { name: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", background: avatarColor(name),
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 600, color: "#fff", flexShrink: 0,
    }}>
      {getInitials(name)}
    </div>
  );
}

function Badge({ type, isDark }: { type: string; isDark: boolean }) {
  const s = getBadgeTheme(type, isDark);
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
}

function getScoreColor(score: number): string {
  if (score >= 90) return "#4ADE80";
  if (score >= 70) return "#A3E635";
  if (score >= 50) return "#FACC15";
  return "#F87171";
}

function extractContactAndPersona(item: any): { contactName: string; persona: string; combined: string } {
  if (!item) return { contactName: '', persona: '', combined: '—' };

  // 1. Check contact object: companyContact, customerContact, customerData
  const c = item.companyContact || item.customerContact || item.customerData;
  let contactName = '';
  if (c) {
    if (c.fullName) {
      contactName = String(c.fullName).trim();
    } else if (c.firstName || c.lastName) {
      contactName = `${c.firstName || c.first_name || ''} ${c.lastName || c.last_name || ''}`.trim();
    } else if (c.name) {
      contactName = String(c.name).trim();
    } else if (c.contactName) {
      contactName = String(c.contactName).trim();
    }
  }

  // 2. Check meeting notes for matched contact / attendees
  const notes = item.meetingNotes;
  if (!contactName && notes) {
    if (typeof notes === 'object') {
      if (notes.matchedContactName) {
        contactName = String(notes.matchedContactName).trim();
      } else if (Array.isArray(notes.attendees) && notes.attendees.length > 0) {
        const firstAtt = notes.attendees.find((a: any) => typeof a === 'string' && a.trim().length > 0 && !a.includes('pitchster.com'));
        if (firstAtt) {
          contactName = String(firstAtt).replace(/<.*?>/, '').trim();
        }
      } else if (notes.organizer && typeof notes.organizer === 'string') {
        contactName = String(notes.organizer).replace(/<.*?>/, '').trim();
      }
    }
  }

  // 3. Fallbacks
  if (!contactName) {
    contactName = (item.contactName || item.customerName || item.otherDetails?.contactName || '').trim();
  }

  // Extract persona
  let persona = '';
  const rawPersona =
    c?.buyerPersona ||
    c?.persona ||
    c?.jobTitle ||
    c?.designation ||
    item.buyerPersona ||
    item.persona ||
    item.personaTitle ||
    item.scenario?.personaTitle ||
    item.TargetCompanyProfile?.buyerPersona ||
    item.TargetCompanyProfile?.persona ||
    item.otherDetails?.buyerPersona ||
    item.otherDetails?.persona ||
    (Array.isArray(item.personaDescriptors) ? item.personaDescriptors[0] : item.personaDescriptors);

  if (Array.isArray(rawPersona) && rawPersona.length > 0) {
    persona = rawPersona.filter(Boolean).join(', ');
  } else if (typeof rawPersona === 'string') {
    persona = rawPersona.trim();
  }

  if (persona.toLowerCase() === contactName.toLowerCase()) {
    persona = '';
  }

  let combined = '—';
  if (contactName && persona) {
    combined = `${contactName} (${persona})`;
  } else if (contactName) {
    combined = contactName;
  } else if (persona) {
    combined = persona;
  }

  return { contactName, persona, combined };
}

function extractScore(raw: any): number {
  if (!raw) return 0;
  if (typeof raw === 'number' && !isNaN(raw) && raw > 0) return raw;

  // Direct candidate fields
  const directCandidates = [
    raw.overallPercentage,
    raw.overallScore,
    raw.score,
    raw.avargerscroing,
    raw.avgScore,
    raw.scoreDetails?.overallScore,
    raw.scoreDetails?.overall,
  ];

  for (const c of directCandidates) {
    if (typeof c === 'number' && !isNaN(c) && c > 0) return c;
    if (typeof c === 'string' && !isNaN(Number(c)) && Number(c) > 0) return Number(c);
    if (typeof c === 'object' && c !== null) {
      const nested = c.overall_percentange ?? c.overall_percentage ?? c.overallScore ?? c.score ?? c.overall;
      if (typeof nested === 'number' && !isNaN(nested) && nested > 0) return nested;
      if (typeof nested === 'string' && !isNaN(Number(nested)) && Number(nested) > 0) return Number(nested);
    }
  }

  // Containers (ai_score, scoring, ai_summary, aiFeedback)
  const containers = [
    raw.ai_score,
    raw.scoring,
    raw.categoryScores?.scores,
    raw.categoryScores,
    raw.feedback?.scores,
    raw.ai_summary?.scores,
    raw.ai_summary?.ai_score,
    raw.aiFeedback?.scores,
    raw.aiFeedback?.ai_score,
  ];

  for (let container of containers) {
    if (!container) continue;
    if (typeof container === 'string') {
      try { container = JSON.parse(container); } catch { continue; }
    }
    if (typeof container === 'number' && container > 0) return container;
    if (typeof container === 'object' && container !== null) {
      const val = container.overall_percentange ?? container.overall_percentage ?? container.overallScore ?? container.overall ?? container.score;
      if (typeof val === 'number' && !isNaN(val) && val > 0) return val;
      if (typeof val === 'string' && !isNaN(Number(val)) && Number(val) > 0) return Number(val);

      // Average category breakdown
      const numbers: number[] = [];
      for (const k of Object.keys(container)) {
        const sub = container[k];
        if (typeof sub === 'number' && sub > 0 && sub <= 100) {
          numbers.push(sub);
        } else if (typeof sub === 'object' && sub !== null) {
          for (const subKey of Object.keys(sub)) {
            const v = sub[subKey];
            if (typeof v === 'number' && v > 0 && v <= 100) {
              numbers.push(v);
            }
          }
        }
      }
      if (numbers.length >= 3) {
        const avg = numbers.reduce((a, b) => a + b, 0) / numbers.length;
        if (avg > 0) return Math.round(avg);
      }
    }
  }

  return 0;
}

function ScorePill({ score, status, isDark }: { score: number; status?: string; isDark: boolean }) {
  if (score && score > 0) {
    const color = getScoreColor(score);
    return <span style={{ fontSize: 14, fontWeight: 700, color }}>{Math.round(score)}</span>;
  }

  const normStatus = String(status || '').toLowerCase();
  if (normStatus.includes('process') || normStatus.includes('transcrib') || normStatus.includes('analyz')) {
    return (
      <span style={{
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: 12,
        background: isDark ? "rgba(56,189,248,0.15)" : "rgba(14,165,233,0.1)",
        color: isDark ? "#38bdf8" : "#0284c7",
        whiteSpace: "nowrap",
      }}>
        Processing
      </span>
    );
  }

  return <span style={{ color: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.25)", fontSize: 13 }}>—</span>;
}

function fmtDate(iso: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const PAGE_SIZE = 15;

export default function AllActivityPage({ onBack, userData }: AllActivityPageProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [page, setPage] = useState(1);

  const { rawMeetings } = useMeetingDashboardData();
  const effectiveTenantId = localStorage.getItem("tenantId") || "";
  const { data: scriptOverviewRes } = useScriptDashboardOverview(effectiveTenantId);

  const allActivity = useMemo(() => {
    const d = userData?.data || userData || {};
    const pitches = d.tenantPitchMeta?.items || [];
    const scenarios = d.tenantScenarios || [];
    const meetings = (Array.isArray(rawMeetings) ? rawMeetings : []).filter(isMeetingRecorded);
    const scriptActivities = scriptOverviewRes?.data?.recentPracticeActivities || [];

    const meetingActivity = meetings
      .map((m: any) => {
        const { contactName, persona, combined } = extractContactAndPersona(m);
        const score = extractScore(m);
        const company =
          m.TargetCompanyProfile?.companyName ||
          m.customerData?.companyName ||
          m.companyName ||
          m.company ||
          m.companyContact?.companyName ||
          m.meetingNotes?.matchedCompanyName ||
          "—";

        const isScheduled =
          String(m.status || '').toLowerCase() === 'scheduled' ||
          String(m.legacyMongoId || '').startsWith('cal-') ||
          String(m.legacyMongoId || '').startsWith('live-');

        return {
          id: m.id || m._id || m.legacyMongoId || m.meetingId || '',
          rep: m.User?.name || m.user?.name || "—",
          type: "Meeting Uploaded",
          details: m.otherDetails?.meetingType || m.meetingType || "Meeting Uploaded",
          company,
          contact: combined,
          contactName,
          persona,
          score,
          status: m.status,
          date: m.createdAt,
          source: "meeting",
          isScheduled,
          raw: m,
        };
      })
      .filter((item) => {
        // Scheduled meetings without score must NEVER be shown
        if (item.isScheduled && (!item.score || item.score <= 0)) {
          return false;
        }
        if (String(item.status || '').toLowerCase() === 'scheduled' && (!item.score || item.score <= 0)) {
          return false;
        }
        return true;
      });

    const pitchActivity = pitches.map((p: any) => {
      const { contactName, persona, combined } = extractContactAndPersona(p);
      const score = extractScore(p);
      const company =
        p.TargetCompanyProfile?.companyName ||
        p.companyName ||
        p.company ||
        p.companyContact?.companyName ||
        "—";

      return {
        id: p.id || p._id || p.legacyMongoId || p.meetingId || p.sessionId || "",
        rep: p.User?.name || "—",
        type: /digital\s*twin/i.test(p.pitchTitle || "")
          ? "Digital Twin"
          : "Pitch Activity",
        details: p.pitchTitle || "—",
        company,
        contact: combined,
        contactName,
        persona,
        score,
        status: p.status,
        date: p.createdAt,
        source: "pitch",
        raw: p,
      };
    });

    const scenarioActivity = scenarios.map((s: any) => {
      const { contactName, persona } = extractContactAndPersona(s);
      const score = extractScore(s);
      const scenarioPersona = s.scenario?.personaTitle || persona || "—";

      return {
        id: s.id || s._id || s.legacyMongoId || s.meetingId || s.sessionId || "",
        rep: s.user?.name || "—",
        type: "Role Play",
        details: s.scenario?.title || "—",
        company: s.scenario?.industry || "—",
        contact: scenarioPersona,
        contactName: contactName || scenarioPersona,
        persona: scenarioPersona,
        score,
        status: s.status,
        date: s.createdAt,
        source: "scenario",
        raw: s,
      };
    });

    const scriptActivity = scriptActivities.map((sa: any) => {
      const completedSec = (sa.sections || []).filter((s: any) => s.isCompleted).length;
      const totalSec = (sa.sections || []).length || 4;
      const score = Math.round((completedSec / totalSec) * 100);
      const contactStr = `${completedSec}/${totalSec} Sections`;
      return {
        id: sa.assignmentId || sa.scriptMetaId,
        rep: sa.repName || "—",
        type: "Script Activity",
        details: sa.scriptTitle || "Script Practice",
        company: sa.scriptType ? sa.scriptType.toUpperCase() : "SCRIPT",
        contact: contactStr,
        contactName: contactStr,
        persona: "",
        score: score,
        status: sa.status,
        date: sa.createdAt,
        source: "script",
        isScript: true,
        raw: sa,
      };
    });

    return [...meetingActivity, ...pitchActivity, ...scenarioActivity, ...scriptActivity]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [userData, rawMeetings, scriptOverviewRes]);

  const filtered = useMemo(() => {
    return allActivity.filter((a) => {
      const q = search.toLowerCase();
      const matchSearch =
        (a.rep || "").toLowerCase().includes(q) ||
        (a.details || "").toLowerCase().includes(q) ||
        (a.company || "").toLowerCase().includes(q) ||
        (a.contact || "").toLowerCase().includes(q) ||
        (a.contactName || "").toLowerCase().includes(q) ||
        (a.persona || "").toLowerCase().includes(q);
      const matchType = typeFilter === "ALL" || a.type === typeFilter;
      return matchSearch && matchType;
    });
  }, [allActivity, search, typeFilter]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allActivity.forEach((a) => { counts[a.type] = (counts[a.type] || 0) + 1; });
    return counts;
  }, [allActivity]);

  const { colors, isDark } = useDashboardTheme();
  const styles = {
    root: { minHeight: "100vh", background: colors.bg, color: colors.text, fontFamily: "'Inter', -apple-system, sans-serif", padding: "28px 36px" },
    backBtn: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: "8px 14px", color: colors.textMuted, fontSize: 13, cursor: "pointer" },
    input: { background: colors.inputBg, border: `1px solid ${colors.inputBorder}`, borderRadius: 8, padding: "8px 14px", color: colors.inputText, fontSize: 13, outline: "none", minWidth: 240 },
    table: { width: "100%", borderCollapse: "collapse" as const },
    th: { fontSize: 11, color: colors.textMuted, fontWeight: 600, letterSpacing: 0.8, padding: "10px 16px", textAlign: "left" as const, borderBottom: `1px solid ${colors.border}` },
    td: { padding: "13px 16px", borderBottom: `1px solid ${colors.borderAlt}`, verticalAlign: "middle" },
    card: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 16, overflow: "hidden" },
    pageBtn: { background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: "6px 12px", color: colors.text, fontSize: 12, cursor: "pointer", transition: "background 0.2s" },
  };

const typeOptions = [
  "ALL",
  "Meeting Uploaded",
  "Pitch Activity",
  "Role Play",
  "Digital Twin",
  "Script Activity",
];
  return (
    <div style={styles.root}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
        <button style={styles.backBtn} onClick={onBack}>← Back to Dashboard</button>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.text, margin: 0 }}>All Activity</h1>
          <p style={{ fontSize: 13, color: colors.textMuted, margin: "3px 0 0" }}>
            {allActivity.length} total activities across all reps
          </p>
        </div>
      </div>

      {/* Type filter chips */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        {typeOptions.map((t) => (
          <button key={t} onClick={() => { setTypeFilter(t); setPage(1); }}
            style={{
              padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: "1px solid",
              borderColor: typeFilter === t ? "#6C63FF" : colors.border,
              background: typeFilter === t ? "rgba(108,99,255,0.2)" : "transparent",
              color: typeFilter === t ? (isDark ? "#A78BFA" : "#5B21B6") : colors.textMuted,
              transition: "all 0.2s",
            }}>
            {t === "ALL" ? `All (${allActivity.length})` : `${t} (${typeCounts[t] || 0})`}
          </button>
        ))}
        <input
          style={{ ...styles.input, marginLeft: "auto" }}
          placeholder="Search rep, title, company..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {/* Table */}
      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>REP</th>
              <th style={styles.th}>ACTIVITY TYPE</th>
              <th style={styles.th}>DETAILS</th>
              <th style={styles.th}>COMPANY / INDUSTRY</th>
              <th style={styles.th}>CONTACT / PERSONA</th>
              <th style={styles.th}>SCORE</th>
              <th style={styles.th}>DATE</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((item: any, i: number) => (
              <tr key={item.id || i}
                style={{ background: i % 2 === 0 ? "transparent" : colors.surfaceAlt, cursor: item.id ? "pointer" : "default" }}
                onClick={() => {
                  if (item.isScript) {
                    navigate(`/script-details/${item.id}?assignedToId=${item.raw?.repId}`, { state: { from: location.pathname + location.search, page: "activity" } });
                  } else if (item.id) {
                    navigate(`/meeting-detail/${item.id}`, { state: { meeting: item.raw, meetingId: item.id, from: location.pathname + location.search, page: "activity" } });
                  }
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = colors.rowHoverAlt}
                onMouseLeave={(e) => e.currentTarget.style.background = i % 2 === 0 ? "transparent" : colors.surfaceAlt}
              >
                <td style={styles.td}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar name={item.rep} size={30} />
                    <span style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>{item.rep}</span>
                  </div>
                </td>
                <td style={styles.td}><Badge type={item.type} isDark={isDark} /></td>
                <td style={styles.td}>
                  <span style={{ fontSize: 13, color: colors.text, fontWeight: 500 }}>{item.details}</span>
                </td>
                <td style={styles.td}>
                  <span style={{ fontSize: 13, color: colors.textMuted }}>{item.company}</span>
                </td>
                <td style={styles.td}>
                  {item.contactName && item.persona && item.contactName !== item.persona ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: colors.text }}>
                        {item.contactName}
                      </span>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: isDark ? "rgba(184,248,144,0.9)" : "#166534"
                      }}>
                        {item.persona}
                      </span>
                    </div>
                  ) : item.contact && item.contact !== "—" ? (
                    <span style={{ fontSize: 13, color: colors.text, fontWeight: 500 }}>
                      {item.contact}
                    </span>
                  ) : (
                    <span style={{ fontSize: 13, color: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.25)" }}>
                      —
                    </span>
                  )}
                </td>
                <td style={styles.td}><ScorePill score={item.score} status={item.status} isDark={isDark} /></td>
                <td style={styles.td}>
                  <span style={{ fontSize: 12, color: colors.textMutedLight }}>{fmtDate(item.date)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: 48, color: colors.textMutedLight, fontSize: 13 }}>
            No activities match your filters
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginTop: 24 }}>
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            style={{ ...styles.pageBtn, opacity: page === 1 ? 0.3 : 1, cursor: page === 1 ? "not-allowed" : "pointer" }}
          >
            ← Previous
          </button>
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>
            Page {page} of {totalPages}
          </span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            style={{ ...styles.pageBtn, opacity: page === totalPages ? 0.3 : 1, cursor: page === totalPages ? "not-allowed" : "pointer" }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
